import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { usePortalStore } from '../contexts/portalStore';
import type {
  PortalActivationDocumentsResponse,
  PortalAgreementRequest,
  PortalAgreementResponse,
  PortalFormAgreementRequest,
} from '../types/onboarding';
import type { PortalFaqsResponse, PortalFaqSearchResult } from '../types/faq';
import type {
  PortalServiceTiersResponse,
  PortalTierChangeResponse,
  PortalPendingTierChangeDto,
} from '../types/portalServiceTier';
import type {
  PortalTicketSettingsDto,
  CreatePortalTicketRequest,
  CreatePortalTicketResponse,
  PortalTicketListItemDto,
} from '../types/portalTicket';

// Types
export interface AccountLookupItem {
  customerAccountId: string;
  tenantId: string;
  accountNumber: string;
  maskedEmail: string;
  maskedPhone?: string;
  hasPhone: boolean;
  tenantName: string;
  customerName?: string;
}

export type MagicLinkDeliveryMethod = 'email' | 'sms';

export interface AccountLookupResponse {
  found: boolean;
  accounts: AccountLookupItem[];
  message?: string;
}

export interface MagicLinkResponse {
  success: boolean;
  message?: string;
  maskedEmail?: string;
  maskedPhone?: string;
  deliveryMethod: MagicLinkDeliveryMethod;
  expirationMinutes: number;
}

export interface PortalAccount {
  customerAccountId: string;
  accountNumber: string;
  tenantId: string;
  tenantSubdomain: string;
  tenantName?: string;
  customerName?: string;
  needsActivation?: boolean;
  accountStatus?: string;
  needsPasswordSetup?: boolean;
}

export interface PortalAuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  account: PortalAccount;
}

// ========== Password Authentication Types ==========

export interface AccountStatusResponse {
  found: boolean;
  hasPassword: boolean;
  isActivated: boolean;
  maskedEmail?: string;
  tenantName?: string;
  customerAccountId?: string;
  tenantId?: string;
  accountNumber?: string;
}

export interface PortalLinkedProvider {
  provider: string; // "Google" | "Microsoft" | "Apple"
  externalEmail: string;
  linkedAt: string;
  lastUsedAt: string | null;
}

export interface PortalTenantCandidate {
  tenantId: string;
  tenantName: string;
  tenantSubdomain: string;
  accountNumberMasked: string;
}

export interface PortalTenantSelectionResponse {
  needsTenantSelection: true;
  selectionToken: string;
  candidates: PortalTenantCandidate[];
}

/**
 * Discriminated union: SSO sign-in may return either a normal portal session
 * OR a list of tenants to pick from (when the same Google identity is linked
 * at multiple ISPs).
 */
export type PortalSsoResponse = PortalAuthResponse | PortalTenantSelectionResponse;

export function isTenantSelection(
  r: PortalSsoResponse,
): r is PortalTenantSelectionResponse {
  return (r as PortalTenantSelectionResponse).needsTenantSelection === true;
}

export interface PortalLoginRequest {
  email?: string;
  accountNumber?: string;
  password: string;
}

export interface SetPasswordRequest {
  password: string;
  confirmPassword: string;
}

export interface ForgotPasswordRequest {
  email?: string;
  accountNumber?: string;
}

export interface ResetPasswordRequest {
  token: string;
  email: string;
  newPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface PasswordResultResponse {
  success: boolean;
  message?: string;
}

export interface ValidateResetTokenResponse {
  valid: boolean;
  email?: string;
  message?: string;
}

export type InvoiceDeliveryMethod = 'Email' | 'Paper' | 'Both';

export interface PortalAccountInfo {
  id: string;
  accountNumber: string;
  customerName: string;
  email?: string;
  phone?: string;
  serviceAddress?: string;
  billingAddress?: string;
  accountBalance: number;
  autopayEnabled: boolean;
  defaultPaymentMethodLast4?: string;
  defaultPaymentMethodType?: string;
  tenantName?: string;
  servicePlanName?: string;
  monthlyRate?: number;
  nextBillingDate?: string;
  invoiceDeliveryMethod?: InvoiceDeliveryMethod;
  paperBillingFee?: number;
  // SMS/Communication Consent
  smsOptIn?: boolean;
  preferredContactMethod?: 'EmailOnly' | 'SmsOnly' | 'Both' | 'NoPreference';
  smsConsentDate?: string;
}

export interface PortalActiveOutage {
  id: string;
  title: string;
  severity: 'Minor' | 'Moderate' | 'Major' | 'Critical';
  status: string;
  startedAt: string;
  estimatedResolutionAt?: string;
  customerMessage?: string;
}

export interface PortalInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
  status: string;
  isPastDue: boolean;
}

export interface PortalInvoiceLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  periodStart?: string;
  periodEnd?: string;
}

export interface PortalInvoiceDetail extends PortalInvoice {
  periodStart: string;
  periodEnd: string;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  lineItems: PortalInvoiceLineItem[];
  pdfUrl?: string;
}

export interface PortalPayment {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  amount: number;
  status: string;
  paymentMethod: string;
  last4?: string;
  invoiceNumber?: string;
}

export interface PortalPaymentMethod {
  id: string;
  type: string;
  isDefault: boolean;
  last4?: string;
  cardBrand?: string;
  expirationMonth?: number;
  expirationYear?: number;
  bankName?: string;
  isExpired: boolean;
}

export interface PortalAutopaySettings {
  isEnabled: boolean;
  paymentMethodId?: string;
  paymentMethodType?: string;
  paymentMethodLast4?: string;
}

export interface PaymentPreferences {
  preferredPayDayOfMonth: number | null;
  nextPaymentOverrideDate: string | null;
  nextPaymentOverrideInvoiceId: string | null;
  calculatedNextPaymentDate: string | null;
  nextUnpaidInvoice: {
    id: string;
    invoiceNumber: string;
    amount: number;
    dueDate: string;
  } | null;
}

export interface UpdatePaymentPreferencesRequest {
  preferredPayDayOfMonth?: number | null;
  nextPaymentOverrideDate?: string | null;
}

export interface PortalMakePaymentRequest {
  invoiceId?: string;
  amount: number;
  paymentMethodId?: string;
  card?: {
    cardNumber: string;
    expirationMonth: string;
    expirationYear: string;
    cvv: string;
    cardholderName: string;
    billingZip?: string;
  };
  bankAccount?: {
    routingNumber: string;
    accountNumber: string;
    accountType: string;
    accountHolderName: string;
  };
  savePaymentMethod: boolean;
}

export interface PortalPaymentResult {
  success: boolean;
  errorMessage?: string;
  paymentId?: string;
  paymentNumber?: string;
  transactionId?: string;
  newBalance?: number;
}

// Stripe-related types
export interface StripeConfig {
  publishableKey: string;
}

export interface PortalFeatures {
  ticketsEnabled: boolean;
  faqEnabled: boolean;
}

export interface PortalTenantBranding {
  tenantName: string;
  subdomain: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  portalAppName: string | null;
  portalAppIconUrl: string | null;
}

// Authorize.Net-related types
export interface AuthorizeNetConfig {
  apiLoginId: string;
  clientKey: string;
  testMode: boolean;
}

export interface ProcessAuthorizeNetPaymentRequest {
  invoiceId?: string;
  amount: number;
  opaqueDataDescriptor: string;
  opaqueDataValue: string;
  savePaymentMethod: boolean;
}

export interface ProcessAuthorizeNetPaymentResponse {
  success: boolean;
  paymentId?: string;
  transactionId?: string;
  errorMessage?: string;
}

export interface CreatePaymentIntentRequest {
  amount: number;
  invoiceId?: string;
  savePaymentMethod: boolean;
}

export interface CreatePaymentIntentResponse {
  success: boolean;
  clientSecret?: string;
  paymentIntentId?: string;
  errorMessage?: string;
}

export interface StripeSavedPaymentMethod {
  id: string;
  type: 'card' | 'us_bank_account';
  last4?: string;
  brand?: string;
  bankName?: string;
  expiryMonth?: number;
  expiryYear?: number;
}

export interface CreateSetupIntentResponse {
  success: boolean;
  clientSecret?: string;
  setupIntentId?: string;
  errorMessage?: string;
}

export interface ConfirmSetupIntentRequest {
  setupIntentId: string;
  setAsDefault: boolean;
}

export interface ConfirmSetupIntentResponse {
  success: boolean;
  paymentMethodId?: string;
  errorMessage?: string;
}

// ========== Installation Scheduling Types ==========

export interface PortalLeadInfo {
  leadId: string;
  customerName: string;
  email?: string;
  phone?: string;
  serviceAddress: string;
  serviceAreaName: string;
  serviceAreaActive: boolean;
  allowPreActivationScheduling: boolean;
  preferredServiceTierId?: string;
  preferredServiceTierName?: string;
}

export interface SchedulingServiceTier {
  id: string;
  name: string;
  description?: string;
  downloadSpeed: number;
  uploadSpeed: number;
  monthlyPrice: number;
  isPreferred: boolean;
  displayOrder: number;
}

export interface PortalSchedulingConfig {
  schedulingGranularity: 'DateOnly' | 'Classic' | 'HourlySlots';
  businessDayStartHour: number;
  businessDayEndHour: number;
  slotDurationMinutes?: number;
  maxAdvanceDays: number;
  minAdvanceDays: number;
}

export interface PortalInstallation {
  id: string;
  confirmationNumber: string;
  status: string;
  scheduledDate: string;
  timeWindow: string;
  scheduledTimeDisplay: string;
  specificStartTime?: string;
  specificEndTime?: string;
  serviceAddress: string;
  serviceTierName?: string;
  serviceTierDownloadSpeed?: number;
  serviceTierUploadSpeed?: number;
  customerNotes?: string;
  technicianNotes?: string;
  canReschedule: boolean;
  canCancel: boolean;
  modificationRestrictedReason?: string;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  calendarEventUrl?: string;
}

export interface PortalInstallationListItem {
  id: string;
  confirmationNumber: string;
  status: string;
  scheduledDate: string;
  scheduledTimeDisplay: string;
  serviceAddress: string;
  canReschedule: boolean;
  canCancel: boolean;
}

export interface PortalSchedulingEligibility {
  canSchedule: boolean;
  reason?: string;
  leadInfo?: PortalLeadInfo;
  serviceTiers: SchedulingServiceTier[];
  schedulingConfig?: PortalSchedulingConfig;
  existingInstallation?: PortalInstallation;
  notSchedulableUntil?: string;
}

export interface ScheduleInstallationRequest {
  scheduledDate: string;
  timeWindow?: string;
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  serviceTierId: string;
  notes?: string;
  acceptedTerms: boolean;
}

export interface ScheduleInstallationResponse {
  success: boolean;
  installationId?: string;
  confirmationNumber?: string;
  errorMessage?: string;
  scheduledDate?: string;
  scheduledTimeDisplay?: string;
  serviceTierName?: string;
  serviceAddress?: string;
  calendarEventUrl?: string;
}

export interface RescheduleInstallationRequest {
  newScheduledDate: string;
  timeWindow?: string;
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  reason?: string;
}

export interface RescheduleInstallationResponse {
  success: boolean;
  errorMessage?: string;
  installation?: PortalInstallation;
}

export interface CancelInstallationResponse {
  success: boolean;
  errorMessage?: string;
}

export interface DateAvailability {
  date: string;
  scheduledCount: number;
  maxCapacity: number;
  remainingSlots?: number;
  isAvailable: boolean;
  isBlocked: boolean;
  blockReason?: string;
}

export interface AvailableDatesResponse {
  dates: DateAvailability[];
  maxInstallationsPerDay: number;
  hasCapacityLimit: boolean;
  blockSaturday: boolean;
  blockSunday: boolean;
}

export interface TimeWindowAvailability {
  window: string;
  displayName: string;
  scheduledCount: number;
  maxCapacity: number;
  remainingSlots?: number;
  isAvailable: boolean;
}

export interface EnhancedDateAvailability extends DateAvailability {
  timeWindows: TimeWindowAvailability[];
}

export interface EnhancedAvailableDatesResponse {
  dates: EnhancedDateAvailability[];
  maxInstallationsPerDay: number;
  hasCapacityLimit: boolean;
  blockSaturday: boolean;
  blockSunday: boolean;
}

export interface HourlySlot {
  startTime: string;
  endTime: string;
  displayName: string;
  scheduledCount: number;
  maxCapacity: number;
  remainingSlots?: number;
  isAvailable: boolean;
}

export interface HourlySlotDay {
  date: string;
  isBlocked: boolean;
  blockReason?: string;
  slots: HourlySlot[];
}

export interface HourlySlotAvailabilityResponse {
  days: HourlySlotDay[];
  slotDurationMinutes: number;
  businessDayStartHour: number;
  businessDayEndHour: number;
}

/**
 * Create portal-specific axios instance
 * This client doesn't include tenant headers since portal endpoints handle tenant via JWT claims
 * SECURITY: withCredentials enables sending HttpOnly cookies for refresh token
 */
export const portalApiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // SECURITY: Required to send/receive HttpOnly cookies cross-origin
});

/**
 * Request interceptor to add portal JWT token
 */
portalApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = usePortalStore.getState();

    console.log('[portalApi] Request:', config.url, 'hasToken:', !!accessToken);

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Token refresh queue
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Public endpoints that should NOT trigger token refresh on 401
// These endpoints are expected to return 401 for invalid credentials, not expired tokens
const PUBLIC_AUTH_ENDPOINTS = [
  '/portal/login',
  '/portal/lookup',
  '/portal/request-link',
  '/portal/verify',
  '/portal/forgot-password',
  '/portal/reset-password',
  '/portal/validate-reset-token',
  '/portal/account-status',
  '/portal/auth/google/authorize',
  '/portal/auth/google/callback',
  '/portal/auth/select-tenant',
];

/**
 * Response interceptor for automatic token refresh
 */
portalApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    console.log('[portalApi] Response error:', {
      status: error.response?.status,
      url: originalRequest?.url,
      hasRetried: originalRequest?._retry,
    });

    // Check if this is a public auth endpoint - don't try to refresh token for these
    const isPublicEndpoint = PUBLIC_AUTH_ENDPOINTS.some(
      (endpoint) => originalRequest?.url?.includes(endpoint)
    );

    // Handle 401 Unauthorized (but not for public auth endpoints)
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isPublicEndpoint
    ) {
      console.log('[portalApi] Got 401, attempting refresh...');
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
            }
            return portalApiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { updateAccessToken } = usePortalStore.getState();

        // SECURITY: Refresh token is sent automatically via HttpOnly cookie
        // No need to retrieve from store or send in body
        console.log('[portalApi] Calling /portal/refresh (token sent via HttpOnly cookie)...');
        const response = await portalApiClient.post<PortalAuthResponse>(
          '/portal/refresh',
          {}
        );
        console.log('[portalApi] Refresh successful');

        const newAccessToken = response.data.accessToken;
        const newExpiresAt = new Date(response.data.expiresAt);

        // Update token in store
        updateAccessToken(newAccessToken, newExpiresAt);

        // Process queued requests
        processQueue(null, newAccessToken);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
        }
        return portalApiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear auth and redirect
        console.log('[portalApi] Refresh FAILED:', refreshError);
        processQueue(
          refreshError instanceof Error
            ? refreshError
            : new Error('Token refresh failed'),
          null
        );
        usePortalStore.getState().clearAuth();

        // Redirect to portal lookup
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/portal')) {
          console.log('[portalApi] Redirecting to /portal?reason=session-expired');
          window.location.href = '/portal?reason=session-expired';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ========== Public Endpoints ==========

export const portalApi = {
  /**
   * Look up account by email or account number
   */
  async lookupAccount(
    email?: string,
    accountNumber?: string
  ): Promise<AccountLookupResponse> {
    const response = await portalApiClient.post<AccountLookupResponse>(
      '/portal/lookup',
      { email, accountNumber }
    );
    return response.data;
  },

  /**
   * Request a magic link via email or SMS
   */
  async requestMagicLink(
    customerAccountId: string,
    tenantId: string,
    email: string,
    deliveryMethod: MagicLinkDeliveryMethod = 'email',
    phone?: string
  ): Promise<MagicLinkResponse> {
    const response = await portalApiClient.post<MagicLinkResponse>(
      '/portal/request-link',
      {
        customerAccountId,
        tenantId,
        email,
        phone,
        deliveryMethod: deliveryMethod === 'sms' ? 1 : 0 // Backend uses enum: Email=0, Sms=1
      }
    );
    return response.data;
  },

  /**
   * Verify magic link using verification code (preferred) or legacy token
   * SECURITY: Verification code is shorter and less exposed in URLs
   */
  async verifyMagicLink(value: string, isCode: boolean = true): Promise<PortalAuthResponse> {
    const response = await portalApiClient.post<PortalAuthResponse>(
      '/portal/verify',
      isCode ? { code: value } : { token: value }
    );
    return response.data;
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<PortalAuthResponse> {
    const response = await portalApiClient.post<PortalAuthResponse>(
      '/portal/refresh',
      { refreshToken }
    );
    return response.data;
  },

  // ========== Password Authentication Endpoints ==========

  /**
   * Get account status (whether password is set, etc.)
   */
  async getAccountStatus(email?: string, accountNumber?: string): Promise<AccountStatusResponse> {
    const params = new URLSearchParams();
    if (email) params.append('email', email);
    if (accountNumber) params.append('accountNumber', accountNumber);

    const response = await portalApiClient.get<AccountStatusResponse>(
      `/portal/account-status?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Login with email/account number and password
   */
  async login(email?: string, accountNumber?: string, password?: string): Promise<PortalAuthResponse> {
    const response = await portalApiClient.post<PortalAuthResponse>(
      '/portal/login',
      { email, accountNumber, password }
    );
    return response.data;
  },

  // ============== External SSO (Google for Phase 1) ==============

  /**
   * Step 1 — get the Google OAuth authorization URL. Frontend redirects the
   * user there; Google sends them back to redirectUri with ?code=...
   */
  async googleAuthorize(redirectUri: string): Promise<{ authorizationUrl: string; state: string }> {
    const response = await portalApiClient.get<{ authorizationUrl: string; state: string }>(
      '/portal/auth/google/authorize',
      { params: { redirectUri } },
    );
    return response.data;
  },

  /**
   * Step 2 — exchange the code for a portal session.
   *
   * Returns either a portal session OR a tenant-chooser payload when the
   * Google identity is linked at multiple tenants. Caller must check via
   * `isTenantSelection(response)` and route accordingly.
   */
  async googleCallback(code: string, redirectUri: string, state?: string): Promise<PortalSsoResponse> {
    const response = await portalApiClient.post<PortalSsoResponse>(
      '/portal/auth/google/callback',
      { code, redirectUri, state },
    );
    return response.data;
  },

  /**
   * Resolves a tenant choice from a previous googleCallback that returned
   * NeedsTenantSelection. The chooser UI POSTs the selection token + chosen
   * tenantId back here and gets a portal session in return.
   */
  async selectTenant(selectionToken: string, tenantId: string): Promise<PortalSsoResponse> {
    const response = await portalApiClient.post<PortalSsoResponse>(
      '/portal/auth/select-tenant',
      { selectionToken, tenantId },
    );
    return response.data;
  },

  /**
   * Authenticated link flow — attach Google to the signed-in customer.
   */
  async linkGoogle(code: string, redirectUri: string): Promise<PortalLinkedProvider> {
    const response = await portalApiClient.post<PortalLinkedProvider>(
      '/portal/auth/google/link',
      { code, redirectUri },
    );
    return response.data;
  },

  async unlinkGoogle(): Promise<void> {
    await portalApiClient.delete('/portal/auth/google/link');
  },

  async listLinkedProviders(): Promise<PortalLinkedProvider[]> {
    const response = await portalApiClient.get<PortalLinkedProvider[]>(
      '/portal/auth/linked-providers',
    );
    return response.data;
  },

  /**
   * Set initial password after magic link verification
   */
  async setPassword(password: string, confirmPassword: string): Promise<PasswordResultResponse> {
    const response = await portalApiClient.post<PasswordResultResponse>(
      '/portal/set-password',
      { password, confirmPassword }
    );
    return response.data;
  },

  /**
   * Request password reset email
   */
  async forgotPassword(email?: string, accountNumber?: string): Promise<PasswordResultResponse> {
    const response = await portalApiClient.post<PasswordResultResponse>(
      '/portal/forgot-password',
      { email, accountNumber }
    );
    return response.data;
  },

  /**
   * Validate password reset token
   */
  async validateResetToken(token: string): Promise<ValidateResetTokenResponse> {
    const response = await portalApiClient.get<ValidateResetTokenResponse>(
      `/portal/validate-reset-token?token=${encodeURIComponent(token)}`
    );
    return response.data;
  },

  /**
   * Reset password using a reset token
   */
  async resetPassword(token: string, email: string, newPassword: string): Promise<PasswordResultResponse> {
    const response = await portalApiClient.post<PasswordResultResponse>(
      '/portal/reset-password',
      { token, email, newPassword }
    );
    return response.data;
  },

  /**
   * Change password while logged in
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
    confirmNewPassword: string
  ): Promise<PasswordResultResponse> {
    const response = await portalApiClient.post<PasswordResultResponse>(
      '/portal/change-password',
      { currentPassword, newPassword, confirmNewPassword }
    );
    return response.data;
  },

  // ========== Protected Endpoints ==========

  /**
   * Get portal features enabled for this tenant
   */
  async getPortalFeatures(): Promise<PortalFeatures> {
    const response = await portalApiClient.get<PortalFeatures>(
      '/portal/features'
    );
    return response.data;
  },

  /**
   * Tenant branding for the portal chrome (logo, colours, portal name). Comes
   * from the public website endpoint, so it needs no auth and returns null for
   * tenants that have not set up a website yet.
   */
  async getTenantBranding(subdomain: string): Promise<PortalTenantBranding | null> {
    try {
      const response = await portalApiClient.get<PortalTenantBranding>(
        `/public/website/${encodeURIComponent(subdomain)}`
      );
      return response.data;
    } catch {
      return null;
    }
  },

  /**
   * Get account info
   */
  async getAccountInfo(): Promise<PortalAccountInfo> {
    const response = await portalApiClient.get<PortalAccountInfo>(
      '/portal/account'
    );
    return response.data;
  },

  /**
   * Update contact info
   */
  async updateContactInfo(data: {
    email?: string;
    phone?: string;
    billingAddress?: string;
  }): Promise<void> {
    await portalApiClient.put('/portal/account/contact', data);
  },

  /**
   * Update invoice delivery preference
   */
  async updateInvoiceDeliveryPreference(deliveryMethod: InvoiceDeliveryMethod): Promise<PortalAccountInfo> {
    const response = await portalApiClient.patch<PortalAccountInfo>('/portal/account/invoice-delivery', {
      invoiceDeliveryMethod: deliveryMethod,
    });
    return response.data;
  },

  /**
   * Update SMS/communication preferences
   */
  async updateCommunicationPreferences(data: {
    smsOptIn: boolean;
    preferredContactMethod: 'EmailOnly' | 'SmsOnly' | 'Both' | 'NoPreference';
  }): Promise<{
    success: boolean;
    message?: string;
    smsOptIn: boolean;
    preferredContactMethod: string;
    smsConsentDate?: string;
  }> {
    const response = await portalApiClient.put('/portal/account/communication-preferences', data);
    return response.data;
  },

  /**
   * Get invoices
   */
  async getInvoices(
    page: number = 1,
    pageSize: number = 20
  ): Promise<PortalInvoice[]> {
    const response = await portalApiClient.get<PortalInvoice[]>(
      '/portal/invoices',
      {
        params: { page, pageSize },
      }
    );
    return response.data;
  },

  /**
   * Get invoice detail
   */
  async getInvoiceDetail(invoiceId: string): Promise<PortalInvoiceDetail> {
    const response = await portalApiClient.get<PortalInvoiceDetail>(
      `/portal/invoices/${invoiceId}`
    );
    return response.data;
  },

  /**
   * Get payment history
   */
  async getPaymentHistory(
    page: number = 1,
    pageSize: number = 20
  ): Promise<PortalPayment[]> {
    const response = await portalApiClient.get<PortalPayment[]>(
      '/portal/payments',
      {
        params: { page, pageSize },
      }
    );
    return response.data;
  },

  /**
   * Make a payment
   */
  async makePayment(
    request: PortalMakePaymentRequest
  ): Promise<PortalPaymentResult> {
    const response = await portalApiClient.post<PortalPaymentResult>(
      '/portal/payments',
      request
    );
    return response.data;
  },

  /**
   * Get stored payment methods
   */
  async getPaymentMethods(): Promise<PortalPaymentMethod[]> {
    const response = await portalApiClient.get<PortalPaymentMethod[]>(
      '/portal/payment-methods'
    );
    return response.data;
  },

  /**
   * Add a payment method
   */
  async addPaymentMethod(data: {
    card?: PortalMakePaymentRequest['card'];
    bankAccount?: PortalMakePaymentRequest['bankAccount'];
    setAsDefault: boolean;
  }): Promise<{ paymentMethodId: string }> {
    const response = await portalApiClient.post<{ paymentMethodId: string }>(
      '/portal/payment-methods',
      data
    );
    return response.data;
  },

  /**
   * Delete a payment method
   */
  async deletePaymentMethod(paymentMethodId: string): Promise<void> {
    await portalApiClient.delete(`/portal/payment-methods/${paymentMethodId}`);
  },

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    await portalApiClient.put(
      `/portal/payment-methods/${paymentMethodId}/default`
    );
  },

  /**
   * Get autopay settings
   */
  async getAutopaySettings(): Promise<PortalAutopaySettings> {
    const response = await portalApiClient.get<PortalAutopaySettings>(
      '/portal/autopay'
    );
    return response.data;
  },

  /**
   * Enable autopay
   */
  async enableAutopay(paymentMethodId: string): Promise<void> {
    await portalApiClient.post('/portal/autopay/enable', { paymentMethodId });
  },

  /**
   * Disable autopay
   */
  async disableAutopay(): Promise<void> {
    await portalApiClient.post('/portal/autopay/disable');
  },

  /**
   * Get payment preferences (custom pay date settings)
   */
  async getPaymentPreferences(): Promise<PaymentPreferences> {
    const response = await portalApiClient.get<PaymentPreferences>(
      '/portal/payment-preferences'
    );
    return response.data;
  },

  /**
   * Update payment preferences (custom pay date settings)
   */
  async updatePaymentPreferences(
    dto: UpdatePaymentPreferencesRequest
  ): Promise<PaymentPreferences> {
    const response = await portalApiClient.patch<PaymentPreferences>(
      '/portal/payment-preferences',
      dto
    );
    return response.data;
  },

  // ========== Stripe Payment Element ==========

  /**
   * Get Stripe configuration (publishable key)
   */
  async getStripeConfig(): Promise<StripeConfig | null> {
    try {
      const response = await portalApiClient.get<StripeConfig>('/portal/stripe-config');
      return response.data;
    } catch {
      // Stripe not configured
      return null;
    }
  },

  /**
   * Get Authorize.Net configuration for Accept.js
   */
  async getAuthorizeNetConfig(): Promise<AuthorizeNetConfig | null> {
    try {
      const response = await portalApiClient.get<AuthorizeNetConfig>('/portal/authorizenet-config');
      return response.data;
    } catch {
      // Authorize.Net not configured
      return null;
    }
  },

  /**
   * Process a payment using Authorize.Net Accept.js opaque data
   */
  async processAuthorizeNetPayment(request: ProcessAuthorizeNetPaymentRequest): Promise<ProcessAuthorizeNetPaymentResponse> {
    const response = await portalApiClient.post<ProcessAuthorizeNetPaymentResponse>(
      '/portal/authorizenet-payment',
      request
    );
    return response.data;
  },

  /**
   * Create a PaymentIntent for Stripe PaymentElement
   */
  async createPaymentIntent(
    request: CreatePaymentIntentRequest
  ): Promise<CreatePaymentIntentResponse> {
    const response = await portalApiClient.post<CreatePaymentIntentResponse>(
      '/portal/create-payment-intent',
      request
    );
    return response.data;
  },

  /**
   * Get saved payment methods from Stripe
   */
  async getStripeSavedPaymentMethods(): Promise<StripeSavedPaymentMethod[]> {
    try {
      const response = await portalApiClient.get<StripeSavedPaymentMethod[]>(
        '/portal/stripe-payment-methods'
      );
      return response.data;
    } catch {
      return [];
    }
  },

  /**
   * Create a SetupIntent for adding a new payment method (PCI-compliant)
   */
  async createSetupIntent(): Promise<CreateSetupIntentResponse> {
    const response = await portalApiClient.post<CreateSetupIntentResponse>(
      '/portal/create-setup-intent'
    );
    return response.data;
  },

  /**
   * Confirm a SetupIntent and save the payment method
   */
  async confirmSetupIntent(
    request: ConfirmSetupIntentRequest
  ): Promise<ConfirmSetupIntentResponse> {
    const response = await portalApiClient.post<ConfirmSetupIntentResponse>(
      '/portal/confirm-setup-intent',
      request
    );
    return response.data;
  },

  /**
   * Logout
   * SECURITY: Refresh token is sent automatically via HttpOnly cookie
   */
  async logout(): Promise<void> {
    const { clearAuth } = usePortalStore.getState();
    try {
      // SECURITY: The HttpOnly cookie is sent automatically
      await portalApiClient.post('/portal/logout', {});
    } catch {
      // Ignore logout errors
    }
    clearAuth();
  },

  // ========== Portal Activation / Onboarding Documents ==========

  /**
   * Get documents that require agreement during portal activation
   */
  async getActivationDocuments(): Promise<PortalActivationDocumentsResponse> {
    const response = await portalApiClient.get<PortalActivationDocumentsResponse>(
      '/portal/activation/documents'
    );
    return response.data;
  },

  /**
   * Submit agreements to onboarding documents during portal activation
   */
  async submitAgreements(request: PortalAgreementRequest): Promise<PortalAgreementResponse> {
    const response = await portalApiClient.post<PortalAgreementResponse>(
      '/portal/activation/agree',
      request
    );
    return response.data;
  },

  /**
   * Submit a form document agreement with form field responses
   */
  async submitFormAgreement(request: PortalFormAgreementRequest): Promise<PortalAgreementResponse> {
    const response = await portalApiClient.post<PortalAgreementResponse>(
      '/portal/activation/form-agreement',
      request
    );
    return response.data;
  },

  // ========== FAQs ==========

  /**
   * Get all FAQs organized by category
   */
  async getFaqs(): Promise<PortalFaqsResponse> {
    const response = await portalApiClient.get<PortalFaqsResponse>('/portal/faqs');
    return response.data;
  },

  /**
   * Search FAQs by query string
   */
  async searchFaqs(query: string): Promise<PortalFaqSearchResult[]> {
    const response = await portalApiClient.get<PortalFaqSearchResult[]>('/portal/faqs/search', {
      params: { q: query },
    });
    return response.data;
  },

  // ========== Service Tiers ==========

  /**
   * Get available service tiers for the customer
   */
  async getServiceTiers(): Promise<PortalServiceTiersResponse> {
    const response = await portalApiClient.get<PortalServiceTiersResponse>(
      '/portal/service-tiers'
    );
    return response.data;
  },

  /**
   * Request a service tier change
   */
  async requestTierChange(newServiceTierId: string): Promise<PortalTierChangeResponse> {
    const response = await portalApiClient.post<PortalTierChangeResponse>(
      '/portal/service-tiers/change',
      { newServiceTierId }
    );
    return response.data;
  },

  /**
   * Get any pending tier change request
   */
  async getPendingTierChange(): Promise<PortalPendingTierChangeDto | null> {
    const response = await portalApiClient.get<PortalPendingTierChangeDto | null>(
      '/portal/service-tiers/pending'
    );
    return response.data;
  },

  // ========== Support Tickets ==========

  /**
   * Get ticket settings (check if enabled for this tenant)
   */
  async getTicketSettings(): Promise<PortalTicketSettingsDto> {
    const response = await portalApiClient.get<PortalTicketSettingsDto>(
      '/portal/tickets/settings'
    );
    return response.data;
  },

  /**
   * Get customer's support tickets
   */
  async getTickets(): Promise<PortalTicketListItemDto[]> {
    const response = await portalApiClient.get<PortalTicketListItemDto[]>(
      '/portal/tickets'
    );
    return response.data;
  },

  /**
   * Create a support ticket
   */
  async createTicket(request: CreatePortalTicketRequest): Promise<CreatePortalTicketResponse> {
    const response = await portalApiClient.post<CreatePortalTicketResponse>(
      '/portal/tickets',
      request
    );
    return response.data;
  },

  // ========== Outages ==========

  /**
   * Get active outages that may affect the customer
   */
  async getActiveOutages(): Promise<PortalActiveOutage[]> {
    const response = await portalApiClient.get<PortalActiveOutage[]>(
      '/portal/outages/active'
    );
    return response.data;
  },

  // ========== Installation Scheduling ==========

  /**
   * Get scheduling eligibility status
   */
  async getSchedulingEligibility(): Promise<PortalSchedulingEligibility> {
    const response = await portalApiClient.get<PortalSchedulingEligibility>(
      '/portal/installation/eligibility'
    );
    return response.data;
  },

  /**
   * Get available installation dates
   */
  async getAvailableInstallationDates(
    startDate?: string,
    endDate?: string
  ): Promise<AvailableDatesResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await portalApiClient.get<AvailableDatesResponse>(
      `/portal/installation/available-dates?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get available installation dates with time windows (Classic mode)
   */
  async getAvailableDatesWithWindows(
    startDate?: string,
    endDate?: string
  ): Promise<EnhancedAvailableDatesResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await portalApiClient.get<EnhancedAvailableDatesResponse>(
      `/portal/installation/available-dates-with-windows?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get available hourly slots (HourlySlots mode)
   */
  async getAvailableHourlySlots(
    startDate?: string,
    endDate?: string
  ): Promise<HourlySlotAvailabilityResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await portalApiClient.get<HourlySlotAvailabilityResponse>(
      `/portal/installation/available-slots?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Schedule an installation
   */
  async scheduleInstallation(
    request: ScheduleInstallationRequest
  ): Promise<ScheduleInstallationResponse> {
    const response = await portalApiClient.post<ScheduleInstallationResponse>(
      '/portal/installation/schedule',
      request
    );
    return response.data;
  },

  /**
   * Get customer's installations
   */
  async getMyInstallations(): Promise<PortalInstallationListItem[]> {
    const response = await portalApiClient.get<PortalInstallationListItem[]>(
      '/portal/installations'
    );
    return response.data;
  },

  /**
   * Get installation details
   */
  async getInstallation(installationId: string): Promise<PortalInstallation> {
    const response = await portalApiClient.get<PortalInstallation>(
      `/portal/installations/${installationId}`
    );
    return response.data;
  },

  /**
   * Reschedule an installation
   */
  async rescheduleInstallation(
    installationId: string,
    request: RescheduleInstallationRequest
  ): Promise<RescheduleInstallationResponse> {
    const response = await portalApiClient.post<RescheduleInstallationResponse>(
      `/portal/installations/${installationId}/reschedule`,
      request
    );
    return response.data;
  },

  /**
   * Cancel an installation
   */
  async cancelInstallation(
    installationId: string,
    reason?: string
  ): Promise<CancelInstallationResponse> {
    const response = await portalApiClient.post<CancelInstallationResponse>(
      `/portal/installations/${installationId}/cancel`,
      { reason }
    );
    return response.data;
  },
};

export default portalApi;
